import {
  propEq,
  uniq,
  curry,
  chain,
  prop,
  map,
  filter,
  compose,
  isEmpty,
  intersection,
  ifElse,
  unless,
  when,
  complement,
  isNil,
  tap,
  findIndex,
  find,
  update,
  adjust,
  evolve,
  identity,
  T,
  whereEq,
  any,
  converge,
  apply,
  zipObj,
  juxt,
  pluck,
  always,
  mergeLeft,
  equals,
  assoc,
  allPass,
  __,
  includes,
  objOf,
  path,
  both,
  gte,
  lte,
  where,
  reduce,
  min,
  max,
  concat,
  toUpper,
  drop,
  take
} from "ramda";

export const isSelected = propEq("selected", true);

export const trace = tap(console.log);

export const isEven = x => x % 2 === 0;

export const capitalize = converge(concat, [compose(toUpper, take(1)), drop(1)]);

export const isNotEmpty = complement(isEmpty);
export const isNotNil = complement(isNil);

export const propNotEq = complement(propEq);

export const mapWhen = curry((pred, whenTrueFn) => map(when(pred, whenTrueFn)));

export const mapUnless = curry((pred, whenFalseFn) =>
  map(unless(pred, whenFalseFn))
);

export const mapIfElse = curry((pred, onTrue, onFalse) =>
  map(ifElse(pred, onTrue, onFalse))
);

const overlap = curry((first, second) =>
  isNotEmpty(intersection(first, second))
);

export const findById = curry((id, items) => find(whereEq({ id }))(items));

export const filterByKey = curry((key, value, items) =>
  filter(whereEq({ [key]: value }))(items)
);
export const rejectByKey = curry((key, value, items) =>
  filter(complement(whereEq({ [key]: value })))(items)
);

export const filterWhere = curry((whereTransform, items) =>
  filter(where(whereTransform))(items)
);
export const findWhere = curry((whereTransform, items) =>
  find(where(whereTransform))(items)
);

export const selectPropByKey = curry((key, value, property) =>
  chain(compose(filterByKey(key, value), prop(property)))
);

export const getIds = curry((key, value, propName, idName, items) =>
  compose(
    uniq,
    chain(prop(idName)),
    selectPropByKey(key, value, propName)
  )(items)
);

export const getLayerIdsByKey = curry((key, value) =>
  compose(uniq, chain(prop("layerIds")), filter(propEq(key, value)))
);

export const applyFnIf = curry((pred, fn, items) =>
  map(ifElse(pred, fn, identity))(items)
);

export const updateBy = curry((key, value, item, items) => {
  const index = findIndex(propEq(key, value))(items);
  return update(index, item)(items);
});

export const updateById = updateBy("id");

export const overlapLayerIds = curry((ids, item) =>
  compose(overlap(ids), prop("layerIds"))(item)
);

export const updateAttributeFeaturesIfElse = curry(
  (pred, whenTrueFn, whenFalseFn) =>
    evolve({
      features: map(ifElse(pred, whenTrueFn, whenFalseFn))
    })
);

export const updateAttributeFeatures = curry(fn =>
  evolve({
    features: fn
  })
);

export const updateAttributeIf = curry((pred, whenTrueFn, whenFalseFn, items) =>
  mapIfElse(pred, whenTrueFn, whenFalseFn)(items)
);

export const hasSelectedFeature = compose(any(isSelected), prop("features"));
export const hasNoSelectedFeature = complement(hasSelectedFeature);

export const convertToFeature = compose(
  apply(zipObj),
  juxt([pluck("field"), pluck("value")])
);

const updateFeature = curry((fields, item) =>
  mergeLeft(convertToFeature(fields))(item)
);

const updateFeatureByFields = curry((x, items) =>
  mapIfElse(
    compose(trace, equals(prop("id", x)), prop("id")),
    always(x),
    identity
  )(items)
);

export const updateFeatureBy = curry((fields, feature, items) =>
  converge(updateFeatureByFields, [updateFeature(fields), always(items)])(
    feature
  )
);

export const hasKeyEq = curry((key, value, item) => propEq(key, value)(item));
export const hasNoKeyEq = complement(hasKeyEq);

export const disable = assoc("disabled");

export const disableWhen = curry(
  (whenKey, whenValue, propKey, propValue, item) =>
    ifElse(
      propEq(whenKey, whenValue),
      ifElse(
        always(propValue),
        mergeLeft({ [propKey]: propValue, [whenKey]: !whenValue }),
        assoc(propKey, propValue)
      ),
      assoc(propKey, propValue)
    )(item)
);

export const disableWhenSelected = disableWhen(
  "selected",
  true,
  "disabled",
  __,
  __
);
export const disableFootprintWhenEnabled = disableWhen(
  "footprintEnabled",
  true,
  "footprintDisabled",
  __,
  __
);

export const updateFeaturesByExtent = curry(
  (visibleIds, allSelectedIds, whenTrueFn, whenFalseFn, features) =>
    updateAttributeFeaturesIfElse(
      ifElse(
        always(isNotEmpty(allSelectedIds)),
        overlapLayerIds(intersection(visibleIds, allSelectedIds)),
        overlapLayerIds(visibleIds)
      ),
      whenTrueFn,
      whenFalseFn
    )(features)
);

export const updateAttributeByExtent = curry(
  (visibleIds, allSelectedIds, whenTrueFn, whenFalseFn, items) =>
    map(
      updateFeaturesByExtent(
        visibleIds,
        allSelectedIds,
        whenTrueFn,
        whenFalseFn
      )
    )(items)
);

export const updateFeaturesBySelection = curry(
  (
    selectedId,
    visibleIds,
    allSelectedIds,
    whenSelectedFn,
    whenTrueFn,
    whenFalseFn,
    attributes
  ) =>
    map(
      ifElse(
        hasKeyEq("id", selectedId),
        whenSelectedFn,
        updateFeaturesByExtent(
          visibleIds,
          allSelectedIds,
          whenTrueFn,
          whenFalseFn
        )
      )
    )(attributes)
);

export const intersectionOrFirst = curry((items1, items2) => {
  if(isNotEmpty(items1) && isNotEmpty(items2))
    return intersection(items1, items2);
  else if(isNotEmpty(items1)) return items1;
  return items2;
});

export const featureIntercept = curry((layerIds, feature) => compose(isNotEmpty, intersection(layerIds), prop("layerIds"))(feature));

export const getAcquisitionYears = curry((ids, items) =>
  compose(filter(compose(includes(__, ids), prop("id"))))(items)
);

export const getAcqYearsIds = curry((acqIds, first, last, items) =>
  compose(
    uniq,
    chain(path(["layerIds"])),
    filter(
      where({
        value: both(gte(__, first), lte(__, last))
      })
    ),
    chain(path(["features"])),
    filter(where({ id: includes(__, acqIds) }))
  )(items)
);

export const findAcqValues = curry((id, items) =>
  compose(
    uniq,
    chain(prop("value")),
    path(["features"]),
    findWhere({ id: equals(id) })
  )(items)
);

const getMinAcqYearByKey = curry((key, acquisition) =>
  compose(reduce(min, Infinity), findAcqValues(key))(acquisition)
);

const getMaxAcqYearByKey = curry((key, acquisition) =>
  compose(reduce(max, -Infinity), findAcqValues(key))(acquisition)
);

export const getAcqLimitYears = acquisition => ({
  first: getMinAcqYearByKey("AcqYearFirst", acquisition),
  last: getMaxAcqYearByKey("AcqYearLast", acquisition)
});

export const getAttributeFeatureIdsByKey = curry((key, value, attribute) =>
  compose(
    uniq,
    chain(prop("layerIds")),
    filterByKey(key, value),
    prop("features")
  )(attribute)
);
